import { Users2 } from "lucide-react";
import { RoleLabelsMap } from "@workflow/types";

import { Skeleton } from "@/components/ui/skeleton";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import { usePessoaResumoCargos } from "@/hooks/pessoa/use-pessoa-resumo-cargos";

const TOP_N = 8;

/** Só o pódio ganha destaque de cor no rank — o resto fica neutro, é ruído senão. */
const RANK_COLOR: Record<number, string> = {
  1: "text-amber-600 dark:text-amber-400",
  2: "text-slate-500 dark:text-slate-400",
  3: "text-orange-700/80 dark:text-orange-400/80",
};

export function CargosResumoList() {
  const { cargos, loading } = usePessoaResumoCargos();
  const top = cargos.slice(0, TOP_N);
  const max = top[0]?.total ?? 1;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users2 className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">Pessoas por cargo</p>
        </div>
        <span className="text-[11px] text-muted-foreground">passe o mouse</span>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-lg" />
          ))}
        </div>
      ) : top.length === 0 ? (
        <p className="flex h-32 items-center justify-center text-center text-sm italic text-muted-foreground">
          Nenhum cargo atribuído ainda.
        </p>
      ) : (
        <div className="space-y-1">
          {top.map((row, i) => (
            <HoverCard key={row.cargo}>
              <HoverCardTrigger
                delay={150}
                className="group grid w-full grid-cols-[20px_1fr_64px_16px] items-center gap-3 rounded-lg bg-transparent p-1.5 text-left transition-colors hover:bg-muted/40"
              >
                <span
                  className={cn(
                    "text-right font-mono text-[11px] font-bold text-muted-foreground/70",
                    RANK_COLOR[i + 1],
                  )}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0 space-y-1">
                  <span
                    className="block truncate text-xs font-medium text-muted-foreground group-hover:text-foreground"
                    title={RoleLabelsMap[row.cargo]}
                  >
                    {RoleLabelsMap[row.cargo] ?? row.cargo}
                  </span>
                  <span className="block h-1.5 overflow-hidden rounded-full bg-muted">
                    <span
                      className="block h-full rounded-full bg-gradient-to-r from-primary to-primary/50 transition-all"
                      style={{ width: `${Math.max((row.total / max) * 100, 4)}%` }}
                    />
                  </span>
                </span>
                <span className="text-right font-mono text-sm font-bold text-foreground">
                  {row.total}
                </span>
                <span />
              </HoverCardTrigger>
              <HoverCardContent className="w-64 p-3.5" side="right" sideOffset={10}>
                <p className="mb-2.5 text-xs font-semibold text-foreground">
                  {RoleLabelsMap[row.cargo] ?? row.cargo}
                </p>
                <div className="flex flex-wrap gap-2">
                  {row.pessoas.map((nome) => (
                    <span
                      key={nome}
                      className="rounded-md border border-border/60 bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground"
                    >
                      {nome}
                    </span>
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          ))}
        </div>
      )}
    </div>
  );
}
